// Case Timeline Page
// BP-10-04: Attorney Frontend

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Filter } from 'lucide-react';
import { useCase, useCaseEvents, useCreateCaseEvent, useDeleteCaseEvent, useCompleteEvent } from '@/features/attorney/hooks/useAttorney';
import { CaseTimeline, EventForm, CaseStatusBadge } from '../components';
import type { CaseStatus, CaseEventFormData } from '@/features/attorney/types/attorney';

export function CaseTimelinePage() {
  const { caseId } = useParams<{ caseId: string }>();
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  const { data: caseData } = useCase(caseId!);
  const { data: eventsData, isLoading } = useCaseEvents(caseId!);
  const createMutation = useCreateCaseEvent(caseId!);
  const deleteMutation = useDeleteCaseEvent(caseId!);
  const completeMutation = useCompleteEvent(caseId!);

  const handleCreateEvent = async (data: CaseEventFormData) => {
    await createMutation.mutateAsync(data);
    setShowEventForm(false);
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (confirm('Are you sure you want to delete this event?')) {
      await deleteMutation.mutateAsync(eventId);
    }
  };

  const handleCompleteEvent = async (eventId: string) => {
    await completeMutation.mutateAsync(eventId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link
            to={`/attorney/cases/${caseId}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-semibold text-navy">Case Timeline</h1>
              {caseData && (
                <CaseStatusBadge status={caseData.status as CaseStatus} />
              )}
            </div>
            <p className="text-gray-600">
              {caseData?.family?.name || 'Case'} •{' '}
              {caseData?.caseNumber || caseData?.internalReference}
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowEventForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Event
        </button>
      </div>

      {/* Event Form Modal */}
      {showEventForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-navy mb-4">Add Event</h2>
              <EventForm
                onSubmit={handleCreateEvent}
                onCancel={() => setShowEventForm(false)}
                isSubmitting={createMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <CaseTimeline
          events={eventsData || []}
          isLoading={isLoading}
          onAddEvent={() => setShowEventForm(true)}
          onEditEvent={(eventId) => setEditingEventId(eventId)}
          onDeleteEvent={handleDeleteEvent}
          onCompleteEvent={handleCompleteEvent}
        />
      </div>
    </div>
  );
}

export default CaseTimelinePage;



